import { RiLoader4Line } from "@remixicon/react";
import { $api } from "@/api/query";
import { syncPhaseLabel } from "@/lib/sync-phase";

interface RunningSync {
	jobId: string;
	jobType: string;
	phase?: string | null;
	processed: number;
	failed: number;
}

interface ActiveSync {
	running?: RunningSync | null;
	resyncPending: boolean;
}

// Global, full-width sync status bar. Lives in the app shell so it persists
// across every page. Polls /sync/active; renders nothing when idle. Built
// generic — any sync_job (scheduled, manual, branch-refresh, loosening
// re-sync) surfaces here. A failed poll keeps the last-known state (the
// query keeps its previous data) and silently retries; it must never flash
// an error bar across the whole app.
export function SyncBar() {
	const { data } = $api.useQuery(
		"get",
		"/api/v1/prohandel/sync/active" as never,
		{
			// Poll while mounted. 5s is responsive without hammering; the
			// endpoint is two cheap indexed reads.
			refetchInterval: 5000,
		} as never,
	);

	const active = data as ActiveSync | undefined;
	if (!active) return null;

	const running = active.running ?? null;

	if (running) {
		const label = syncPhaseLabel(running.phase);
		return (
			<output
				aria-atomic="true"
				className="flex items-center gap-3 border-b bg-primary/5 px-4 py-2 text-sm"
			>
				<RiLoader4Line className="size-4 shrink-0 animate-spin text-primary" />
				<span className="font-medium">Prohandel sync</span>
				<span className="text-muted-foreground">{label}</span>
				<span className="ml-auto tabular-nums text-muted-foreground">
					{running.processed.toLocaleString()} processed
					{running.failed > 0 ? ` · ${running.failed.toLocaleString()} failed` : ""}
				</span>
			</output>
		);
	}

	if (active.resyncPending) {
		return (
			<output
				aria-atomic="true"
				className="flex items-center gap-3 border-b bg-muted/40 px-4 py-2 text-sm text-muted-foreground"
			>
				<RiLoader4Line className="size-4 shrink-0 animate-spin" />
				<span>Re-sync queued — starts when the current sync finishes.</span>
			</output>
		);
	}

	return null;
}
